/**
 * agGridErrorTracker — single helper every AG Grid Infinite Row Model
 * datasource calls instead of `failCallback()` directly.
 *
 * Day 47 Phase 3 — surfaces silent datasource failures (e.g., RPC errors,
 * 4xx/5xx, network blips) into a window-level audit channel so the
 * arbi-test.cjs runtime audit can detect them as RUNTIME_FAIL rather than
 * letting them slip past as empty grids. Surfaced by UAT round 1's
 * discrepancy queue ate-the-error finding (BACKLOG STANDARDS &
 * DOCUMENTATION → "AG Grid silent failCallback audit rule").
 *
 * Pattern at every call site:
 *
 *     try {
 *       const { data, error, count } = await supabase.rpc(...);
 *       if (error) throw error;
 *       successCallback(data, count ?? null);
 *     } catch (err) {
 *       reportDatasourceFailure(
 *         'Products',
 *         { rpcName: 'list_products', params: { startRow, endRow } },
 *         err,
 *         () => params.failCallback()
 *       );
 *     }
 *
 * `pageName` is the page's display name from /standards/UI_STANDARDS.md
 * Section 1 (e.g. "Products", "Purchase Orders", "Receiving"). It's the
 * label arbi-test.cjs uses when emitting the RUNTIME_FAIL finding.
 */

export interface DatasourceFailureContext {
  rpcName?: string;
  /** Free-form params snapshot for diagnosis. Avoid sensitive values. */
  params?: unknown;
}

interface DatasourceFailureEntry {
  page: string;
  rpcName: string | null;
  params: unknown;
  error: string;
  timestamp: string;
}

declare global {
  interface Window {
    __arbiRuntimeFails?: DatasourceFailureEntry[];
  }
}

export function reportDatasourceFailure(
  pageName: string,
  context: DatasourceFailureContext,
  error: unknown,
  failCallback: () => void
): void {
  const entry: DatasourceFailureEntry = {
    page: pageName,
    rpcName: context.rpcName ?? null,
    params: context.params ?? null,
    error: error instanceof Error ? error.message : String(error),
    timestamp: new Date().toISOString(),
  };

  if (typeof window !== 'undefined') {
    window.__arbiRuntimeFails = window.__arbiRuntimeFails ?? [];
    window.__arbiRuntimeFails.push(entry);
  }

  // Always console.error for live dev visibility — even when arbi-test.cjs
  // isn't running, this surfaces in the browser console.
  // eslint-disable-next-line no-console
  console.error(`[ArbiAudit] Datasource failure on ${pageName}:`, entry);

  failCallback();
}
